import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack } from '@mui/material';
import { useState } from 'react';

import { Text, isDefined } from 'common';

import { BlockValueTemplateFieldArea } from './CodeMirror/FlowTemplate';
import { type ConfigFieldProps } from './types';
import { useSelectedNodeField } from './useSelectedNodeField';
import { useReactFlowViewContext } from '../../context/context';

enum DocumentMode {
  Html = 'Html',
  PlainText = 'PlainText',
}

interface FieldProps extends ConfigFieldProps {
  label: string;
  documentMode?: DocumentMode;
  formatHtml?: (html: string) => string;
}

export function BlockHtmlField(props: FieldProps) {
  const {
    fieldName,
    label,
    required = false,
    documentMode = DocumentMode.Html,
    formatHtml,
  } = props;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [mode, setMode] = useState<DocumentMode>(documentMode);
  const { value, isFieldErrorVisible, visibleError } = useSelectedNodeField<string>(fieldName);
  const { isEditable } = useReactFlowViewContext();

  return (
    <>
      <Stack spacing={1}>
        <Text variant="heading6">{label}</Text>
        <Button variant="outlined" onClick={() => setIsDialogOpen(true)}>
          Open HTML
        </Button>
        {isFieldErrorVisible && (
          <Text variant="monospace" color="error">
            {visibleError}
          </Text>
        )}
      </Stack>
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} fullScreen>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <IconButton aria-label="Close" onClick={() => setIsDialogOpen(false)}>
            <ArrowBackIcon />
          </IconButton>
          <span>{label}</span>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} height="100%">
            <Stack alignItems="flex-end">
              <Button
                variant="contained"
                onClick={() =>
                  setMode((mode) =>
                    mode === DocumentMode.Html ? DocumentMode.PlainText : DocumentMode.Html
                  )
                }
              >
                {mode === DocumentMode.Html
                  ? isEditable
                    ? 'Edit Plain Text'
                    : 'View Plain Text'
                  : 'View HTML'}
              </Button>
            </Stack>
            {mode === DocumentMode.Html && (
              <Box flex={1}>
                <iframe
                  name="html-preview"
                  src={`data:text/html;charset=utf-8,${isDefined(value) ? encodeURIComponent(formatHtml?.(value) ?? value) : ''}`}
                  width="100%"
                  height="100%"
                  sandbox=""
                />
              </Box>
            )}
            {mode === DocumentMode.PlainText && (
              <BlockValueTemplateFieldArea
                fieldName={fieldName}
                aria-label={label}
                required={required}
                minHeight="80vh"
              />
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
